import type {CapturedEvent, ConsoleCapture} from './capture';
import type {FrontendLogRecord} from './record';

/** Levels the application can be configured to. `fatal` and `silent` are not offered. */
const configuredLevels = ['trace', 'debug', 'info', 'warn', 'error'] as const;

export type ConfiguredLevel = (typeof configuredLevels)[number];

/** The logging configuration a host selects before the application starts. */
export interface LoggingConfiguration {
    /** The minimum level of the application's logger. */
    readonly minLevel: ConfiguredLevel;
    /**
     * Receives every redacted logger event as a record. Absent when the host
     * does not accept records. A host that selects a configuration also turns
     * off the in-memory log, so nothing is retained either way.
     */
    readonly forward?: (record: FrontendLogRecord) => void;
    /**
     * The host's console capture. Its events are redacted like logger events
     * and sent to `forward`, so it is only accepted together with `forward`.
     */
    readonly capture?: ConsoleCapture;
}

export interface ConfigurationSlot {
    /**
     * Selects the configuration. Only the first valid call succeeds, and only
     * before the logger initializes.
     *
     * @throws When a value was already set, the logger already initialized, or
     * the value does not match the required schema. Nothing is retained from a
     * rejected value.
     */
    set: (input: unknown) => void;
    /**
     * Returns the selected configuration, or `undefined` when none was set,
     * and permanently closes the slot to further changes.
     */
    seal: () => LoggingConfiguration | undefined;
}

type SlotState =
    | {status: 'open'}
    | {status: 'set'; value: LoggingConfiguration}
    | {status: 'sealed'; value: LoggingConfiguration | undefined};

const isConfiguredLevel = (value: unknown): value is ConfiguredLevel =>
    configuredLevels.some(level => level === value);

const invalid = () => new Error('LoggingConfiguration: invalid value');

/**
 * Parses the host's console capture, reading each method once and binding it
 * to the host object, so later changes to that object have no effect.
 */
const parseCapture = (input: unknown): ConsoleCapture => {
    if (typeof input !== 'object' || input === null) {
        throw invalid();
    }
    const attach = 'attach' in input ? input.attach : undefined;
    const withoutCapture =
        'withoutCapture' in input ? input.withoutCapture : undefined;
    if (typeof attach !== 'function' || typeof withoutCapture !== 'function') {
        throw invalid();
    }

    return Object.freeze({
        attach: (listener: (event: CapturedEvent) => void) => {
            attach.call(input, listener);
        },
        withoutCapture: (output: () => void) => {
            withoutCapture.call(input, output);
        },
    });
};

/**
 * Parses the value a host passes to the setter into a frozen
 * {@link LoggingConfiguration}. The value must be a plain object with exactly
 * `minLevel`, optionally `forward`, and optionally `capture`, which is only
 * accepted together with `forward`. `minLevel` must be one of the configured
 * levels, `forward` a function, and `capture` an object with `attach` and
 * `withoutCapture` methods. Each field is read once.
 *
 * @throws A generic error, without any of the input, when the value does not
 * match.
 *
 * @example
 * parseConfiguration({minLevel: 'debug', forward: record => send(record)});
 * // {minLevel: 'debug', forward: [Function]}
 * parseConfiguration({minLevel: 'debug', capture: bridge}); // throws
 */
const parseConfiguration = (input: unknown): LoggingConfiguration => {
    if (typeof input !== 'object' || input === null || Array.isArray(input)) {
        throw invalid();
    }

    const fields = Object.keys(input);
    const hasForward = fields.includes('forward');
    const hasCapture = fields.includes('capture');
    const expectedFields = 1 + Number(hasForward) + Number(hasCapture);

    if (
        fields.length !== expectedFields ||
        !fields.includes('minLevel') ||
        !('minLevel' in input) ||
        (hasCapture && !hasForward)
    ) {
        throw invalid();
    }

    // Each field is read once, so a getter cannot answer differently later.
    const minLevel = input.minLevel;
    const forward = 'forward' in input ? input.forward : undefined;
    const capture = 'capture' in input ? input.capture : undefined;

    if (!isConfiguredLevel(minLevel)) {
        throw invalid();
    }
    if (!hasForward) {
        return Object.freeze({minLevel});
    }
    if (typeof forward !== 'function') {
        throw invalid();
    }
    // A function value is accepted as the host's record callback.
    const forwardRecord = forward as (record: FrontendLogRecord) => void;
    if (!hasCapture) {
        return Object.freeze({minLevel, forward: forwardRecord});
    }

    return Object.freeze({
        minLevel,
        forward: forwardRecord,
        capture: parseCapture(capture),
    });
};

/**
 * Creates a slot that holds one logging configuration, set at most once and
 * only before it is sealed.
 *
 * @example
 * const slot = createConfigurationSlot();
 * slot.set({minLevel: 'info', forward: record => send(record)});
 * slot.seal(); // {minLevel: 'info', forward: ...}
 * slot.set({minLevel: 'trace'}); // throws
 */
export const createConfigurationSlot = (): ConfigurationSlot => {
    let state: SlotState = {status: 'open'};

    const rejectUnlessOpen = (): void => {
        switch (state.status) {
            case 'open':
                return;
            case 'set':
                throw new Error('LoggingConfiguration: already set');
            case 'sealed':
                throw new Error(
                    'LoggingConfiguration: logger already initialized',
                );
            default: {
                const unreachable: never = state;
                throw new Error(
                    `LoggingConfiguration: unexpected state ${String(unreachable)}`,
                );
            }
        }
    };

    return {
        set: input => {
            rejectUnlessOpen();
            const value = parseConfiguration(input);
            // Parsing reads the caller's properties, which may have re-entered
            // the slot, so the state must be checked again before committing.
            rejectUnlessOpen();
            state = {status: 'set', value};
        },
        seal: () => {
            switch (state.status) {
                case 'open':
                    state = {status: 'sealed', value: undefined};
                    return undefined;
                case 'set':
                    state = {status: 'sealed', value: state.value};
                    return state.value;
                case 'sealed':
                    return state.value;
                default: {
                    const unreachable: never = state;
                    return unreachable;
                }
            }
        },
    };
};

/**
 * The process-wide slot. The public `./logging-configuration` entry exposes
 * only its setter; the logger module seals it when it initializes.
 */
export const loggingConfiguration = createConfigurationSlot();
